// @flow

declare var Mazemap: any;
declare var document: any;

import { WrapReactComponent } from "components/WrapReactComponent";
import type { LngLat } from "Shared/LocationDescriptor/LocationDescriptor";

var Common = require('utils/Common');
import { STATE_ENUMS, KioskStateController, generateKioskShareUrlFromState } from "kiosk/KioskState";
import GenericMapControl from "kiosk/GenericMapControl";
import GenericUIButton from "kiosk/GenericUIButton";
import MultiPoisMarkerLayer from "kiosk/MultiPoisMarkerLayer";
import { CategoriesList as CategoriesList_react } from "kiosk/CategoriesList/CategoriesList";
const CategoriesList = WrapReactComponent(CategoriesList_react);

import YouAreHereLayer from "kiosk/YouAreHereLayer";
import AutoReset from "kiosk/AutoReset";
import { RouteInfoBox } from "kiosk/RouteInfoBox";
import { QRCodeBox } from "kiosk/QRCodeBox";

require("./mazebox-custom.scss");
require("./kiosk.scss");
require("./layout.scss");

function poi2PointFeature( poi ){
    // Convert a flat poi to a GeoJSON feature with a geometry
    let feature = {"type": "Feature", properties: poi, geometry: poi.geometry};
    feature.properties.zLevel = poi.z;
    return feature;
}



function appViewTemplate (layout = "horizontal"){
    return `
        <div class="kiosk-layout-wrapper `+ layout +`">
            <div id="sidebar">
                <div class="sidebar-header">
                    <div id="search-input-container" style="display: none;" class='search-control-default'>
                        <input tabindex="0" id="searchInput" class="search-input no-focus" autocomplete="off" type="search" name='search' placeholder='Search'>
                    </div>
                </div>
                <div class="sidebar-content">
                    <div id="suggestions" class="search-suggestions default"></div>
                    <div id="categories-list" class=""></div>
                </div>

            </div>
            <div id="map" aria-label="map view" tabindex="1" class="mazemap"></div>
        </div>
    `;
}

type KioskAppOptions = {
    kioskcenter:LngLat,
    buildingid: number,
    resetUrl:   string,
    campusid:   number,
    poitypes:   Array<number>,
    bearing:    number,
    timeout:    number,
    zlevel:     number,
    layout:     string,
    zoom:       number,
    pitch:      number,
    center:     LngLat,
    analytics:  boolean,
};

type YouAreHereDotConfig = {
    center: LngLat,
    markerOptions: {
        zLevel: number,
        offset: [number, number],
    },
    bearing: number,
    pitch: number,
    zoom: number,
    zLevel: number,
};

export default class KioskApp{
    initOptions: KioskAppOptions;
    map: Mazemap.Map;
    autoReset: ?AutoReset;
    stateController: KioskStateController;
    qrCodeBox: QRCodeBox;
    _onMapMoveDebounced: () => void;
    routeController: Mazemap.RouteController;
    routeInfoBox: RouteInfoBox;
    poiMarker: Mazemap.MazeMarker;
    youAreHereDotConfig: YouAreHereDotConfig;
    tracker: Mazemap.Analytics.tracker;
    multiPoisLayer: MultiPoisMarkerLayer;
    mySearchInput = Mazemap.Search.SearchInput;

    constructor (containerElement: HTMLElement, initOptions: KioskAppOptions) {
        if( initOptions.analytics === false ){
            // Globally deactivate the analytics module in the JS API
            window.Mazemap.Analytics.deactivate();
        }

        this.tracker = window.Mazemap.Analytics.tracker;
        this.tracker.set('dimension1', 'KioskApp');
        this.tracker.pageView();

        this.initOptions = initOptions;
        this._onMapMoveDebounced = Common.debounce( () => {
            this.stateController.setState({
                zoom: this.map.getZoom(),
                bearing: this.map.getBearing(),
            });
        }, 500 );

        containerElement.insertAdjacentHTML('beforeend', appViewTemplate(initOptions.layout) );

        // Just the same way to initialize as always...
        this.map = new Mazemap.Map({
            style: "mapbox://styles/mazemap/cjtpz3o4j4uwq1fp0iebgunuw?cachebuster=4",
            container: 'map',
            campuses: initOptions.campusid,
            center: initOptions.center,
            zoom: initOptions.zoom,
            zLevel: initOptions.zlevel,
            pitch: initOptions.pitch,
            bearing: initOptions.bearing,
            zLevelControl: false,
            scrollZoom: true,
            doubleClickZoom: false,
            touchZoomRotate: true,
            dragRotate: true,
            minZoom: 16,
            hideMapboxLogo: true,
            disableAttributionLinks: true
        });
        if( initOptions.timeout > 0 ){
            this.autoReset = new AutoReset({
                element: document.body,
                timeout: initOptions.timeout,
                onReset: this.reset.bind(this),
            });
        }


        this.poiMarker = new Mazemap.MazeMarker({
            color: 'red',
            innerCircle: true,
            innerCircleColor: '#FFF',
            size: 60,
            innerCircleScale: 0.5,
            zLevel: 1
        })

        this.youAreHereDotConfig = {
            center: initOptions.kioskcenter,
            markerOptions: {
                zLevel: initOptions.zlevel,
                offset: [0,-29]
            },
            bearing: initOptions.bearing,
            pitch: initOptions.pitch,
            zoom: initOptions.zoom,
            zLevel: initOptions.zlevel
        };


        this.stateController = new KioskStateController( {
            state: STATE_ENUMS.DEFAULT,
            campusid: initOptions.campusid,
            zoom: initOptions.zoom,
            bearing: initOptions.bearing,
            pitch: initOptions.pitch,
            start: { lngLat: this.youAreHereDotConfig.center, zLevel: this.youAreHereDotConfig.zLevel },
            avoidStairs: false,
        });

        /* UI BUTTONS CONTAINER */
        var uiButtonsContainer = document.createElement("div");
        uiButtonsContainer.className = "ui-buttons-container";


        /* ZOOM BUTTONS */
        var zoomInBtn = new GenericUIButton({ className: 'zoom-button in', onClick: this.map.zoomIn.bind(this.map) });
        zoomInBtn.setHTML('ZOOM IN');
        uiButtonsContainer.appendChild( zoomInBtn.getContainer() );

        var zoomOutBtn = new GenericUIButton({ className: 'zoom-button out', onClick: this.map.zoomOut.bind(this.map) });
        zoomOutBtn.setHTML('ZOOM OUT');
        uiButtonsContainer.appendChild( zoomOutBtn.getContainer() );

        /* RESET APP BUTTON */
        var resetUIButton = new GenericUIButton({
            className: 'reset-button',
            onClick: () => {
                this.tracker.sendEvent('UI','click reset Button');
                setTimeout(this.reset.bind(this),20);
            }
        });
        resetUIButton.setHTML('RESET');
        uiButtonsContainer.appendChild( resetUIButton.getContainer() );


        /* APPEND UI BUtTONS TO GENERIC MAP CONTROL*/
        const uiMapControl = new GenericMapControl({className: 'mapboxgl-ctrl-group map-ui-buttons-container'});
        const uiRouteInfoControl = new GenericMapControl({className: 'mapboxgl-ctrl-group map-ui-buttons-container'});
        const uiQRCodeControl = new GenericMapControl({className: 'mapboxgl-ctrl-group map-ui-buttons-container'});

        let uiMapControlPosition;
        let uiRouteInfoControlPosition;
        let uiQRCodeControlPosition;

        if(initOptions.layout === 'horizontal'){
            uiMapControlPosition ='top-left';
            uiRouteInfoControlPosition = 'bottom-right';
            uiQRCodeControlPosition = 'bottom-right';
        }else if(initOptions.layout === 'vertical'){
            uiMapControlPosition ='bottom-left';
            uiRouteInfoControlPosition = 'top-right';
            uiQRCodeControlPosition = 'top-right';
        }
        this.map.addControl(uiMapControl, uiMapControlPosition);
        this.map.addControl(uiQRCodeControl, uiQRCodeControlPosition);
        this.map.addControl(uiRouteInfoControl, uiRouteInfoControlPosition);

        uiMapControl.getContainer().appendChild( uiButtonsContainer );

        this.routeInfoBox = new RouteInfoBox((avoidStairs: boolean) => {
            this.setRouteAvoidStairs(avoidStairs);
        });
        uiRouteInfoControl.getContainer().appendChild( this.routeInfoBox.render() );

        this.qrCodeBox = new QRCodeBox();
        uiQRCodeControl.getContainer().appendChild( this.qrCodeBox.render() );


        this.stateController.events.on('change', Common.debounce( this.onStateChange.bind(this), 500 ) );

        this.map.layerEventHandler.on('click', 'mm-floor-outline', (e: Object) => {
            // Clicked on an indoor floor
            this.onMapClick(e, this.map.getZLevel() );
        });

        this.map.layerEventHandler.on('click', null, (e: Object) => {
            // Clicked on something else, ie. outdoors
            this.onMapClick(e, 0);
        });

        this.map.layerEventHandler.on('click', null, this.onMapClick.bind(this) );

        this.map.on('load', () => {


            /* FLOOR CONTROL */
            var floorBarPosition = 'top-left';
            if(initOptions.layout === 'vertical'){
                floorBarPosition = 'bottom-left';
            }
            var floorBar = new Mazemap.ZLevelBarControl( {
                className: 'custom-zlevel-bar',
                maxHeight: 300,
                buttonHeight: 60,
                autoUpdate: true,
            } );
            this.map.addControl( floorBar, floorBarPosition );


            // /* ZOOM CONTROL */
            // var navigationControl = new Mazemap.mapboxgl.NavigationControl();
            // navigationControl._compass.remove();
            // this.map.addControl(navigationControl, 'middle-right');

            var onResize = () => {
                var height = this.map.getCanvas().clientHeight;
                var maxHeight = height - 100; // 100 pixels account for margins and spacing
                floorBar.setMaxHeight(maxHeight);
            }

            this.map.on('resize', onResize);

            onResize();

            initSearch();

            this.multiPoisLayer = new MultiPoisMarkerLayer(this.map);

            this.multiPoisLayer.onClick = (e, pois) => {
                var feature = pois[0];
                var poiId = feature.properties.poiId;
                if(poiId){
                    Mazemap.Data.getPoi(poiId).then((poi) => {
                        this.setSelectedPoi(poi);
                    });
                }
            }

            new Promise( function(resolve){

                if(initOptions.poitypes){
                    return resolve(initOptions.poitypes);
                }else{
                    return Mazemap.Data.getCampus(initOptions.campusid)
                    .then( function( campus ){
                        return resolve(campus.properties.poiTypesSelectableInApp);
                    })
                }
            }).then( (poiTypes) => {

                if(!poiTypes){
                    return;
                }
                CategoriesList({
                    campusId: initOptions.campusid,
                    poiTypes: poiTypes,
                    buildingId: initOptions.buildingid,
                    onPoiClick: (pois, typeItem)=>{
                        this.tracker.sendEvent('UI','Click menu item',typeItem.type.title);
                        this.setSelectedPoi(null);
                        if(pois.length === 1){
                            var poi = pois[0];
                            var poiId = poi.poiId;
                            Mazemap.Data.getPoi(poiId).then((poi)=>{
                                this.setSelectedPoi(poi);
                            });
                        }else{
                            var features = pois.map(poi2PointFeature);
                            this.multiPoisLayer.setPois(features);
                            var featureCollection = {type: "FeatureCollection", features: features};
                            var bbox = Mazemap.Util.Turf.bbox(featureCollection);
                            var bounds = new Mazemap.LngLatBounds([bbox[0],bbox[1]],[bbox[2],bbox[3]]);
                            bounds.extend([this.youAreHereDotConfig.center.lng, this.youAreHereDotConfig.center.lat]);
                            this.map.fitBoundsRotated(bounds, {padding: 200});

                        }
                    }
                }, document.getElementById("categories-list"));

            })

            new YouAreHereLayer({
                map: this.map,
                lngLat: this.youAreHereDotConfig.center,
                zoom: this.youAreHereDotConfig.zoom,
                bearing: this.youAreHereDotConfig.bearing,
                pitch: this.youAreHereDotConfig.pitch,
                zLevel: this.youAreHereDotConfig.zLevel
            });


            // Initialize a Highlighter for POIs
            // Storing the object on the map just makes it easy to access for other things
            this.map.highlighter = new Mazemap.Highlighter( this.map, {
                showOutline: true, // optional
                showFill: true, // optional
                outlineColor: '#565d78', // optional
                fillOpacity: 1,
                fillColor: '#565d78'  // optional
            } );
            //new Mazemap.ZLevelMarker( youAreHereDotConfig.markerOptions ).setLngLat( youAreHereDotConfig.lngLat ).addTo(this.map);


            /* CUSTOM ROUTE STYLE */

            this.routeController = new Mazemap.RouteController(this.map, {
                routeLineColorPrimary: '#565d78',
                routeLineColorSecondary: '#888888',
                lineWidth: 24,
                highlightLineWidth: 12,
                highlightOpacity: 0.15
            });

            this.onStateChange();

        });


        this.map.on('moveend', this._onMapMoveDebounced);


        var initSearch = () =>{

            var mySearch = new Mazemap.Search.SearchController({
                campusid: initOptions.campusid,

                rows: 30,

                withpois: true,
                withbuilding: false,
                withtype: false,
                withcampus: false,

                resultsFormat: 'geojson'
            });

            this.mySearchInput = new Mazemap.Search.SearchInput({
                container: document.getElementById('search-input-container'),
                input: document.getElementById('searchInput'),
                suggestions: document.getElementById('suggestions'),
                searchController: mySearch
            });
            this.mySearchInput.on('itemclick', (e)=>{
                var poiFeature = e.item;

                Mazemap.Data.getPoi( poiFeature.properties.poiId).then( poi => {
                        // Run custom highlight function
                        this.setSelectedPoi(poi);
                        this.tracker.sendEvent('SEARCH','Click search result',poi.properties.names[0]+', '+poi.properties.buildingName);

                }).catch( function(){ return false; } );

            });

            this.map.getCanvas().addEventListener('focus', ()=>{
                this.mySearchInput.hideSuggestions();
            });


            const searchInputContainer = document.getElementById('search-input-container');
            if( searchInputContainer){
                searchInputContainer.style.display = '';
            }else{
                console.error("Expected to find searchInputContainer"); //eslint-disable-line
            }

        }

    }

    onMapClick(e: Object, zLevel: number) {
        // un-highlight any already highlighted rooms
        this.setSelectedPoi(null);

        this.multiPoisLayer.setPois(null);
        this.mySearchInput.clearSuggestions();
        this.mySearchInput._input.value = '';

        var lngLat = e.lngLat;

        // Fetching via Data API
        // NB: Adding optional campusId parameter, makes lookup much faster, but can be omitted
        Mazemap.Data.getPoiAt(lngLat, zLevel).then( poi => {
            // Run custom highlight function
            this.setSelectedPoi(poi);
            this.tracker.sendEvent('Interaction','mapClick',poi.properties.names[0]+', '+poi.properties.buildingName);
        }).catch( () => {
            return false;
        } );
    }

    highlightRoom(poi: ?Object){
        // If the POI has a polygon, use the default 'highlight' function to draw a marked outline around the POI.
        if(poi && poi.geometry.type === "Polygon"){
            this.map.highlighter.highlight(poi);
        }else{
            this.map.highlighter.clear();
        }
    }

    setRoute( start: ?Object, dest: ?Object ){
        this.routeController.clear();

        this.routeInfoBox.setState({route: null});

        if(start === null){
            return;
        }
        const avoidStairs = this.stateController.getAvoidStairs();
        return Mazemap.Data.getRouteJSON(start, dest, {avoidStairs: avoidStairs, connectToEnd: false, connectToStart: false, simplifyLine: true})
        .then( (geojsonRoute) => {
            this.routeController.setPath(geojsonRoute);
            this.routeInfoBox.setState({route: geojsonRoute, avoidStairs: avoidStairs});
            this.fitToFeatureBounds(geojsonRoute, 50);
            return geojsonRoute;
        })
    }

    setRouteAvoidStairs( avoidStairs: boolean ){
        const start = this.stateController.getStart();
        const dest = this.stateController.getDest();
        this.stateController.setAvoidStairs( avoidStairs);
        this.setRoute(start, dest);
    }

    setSelectedPoi (poi: ?Object) {
        this.highlightRoom(null);
        this.setRoute(null);

        if(this.poiMarker){
            this.poiMarker.remove();
        }

        if(!poi){
            this.stateController.setState({
                state: STATE_ENUMS.DEFAULT,
            });
            return;
        }

        // Get a center point for the POI, because the data can return a polygon instead of just a point sometimes
        var lngLat = Mazemap.Util.getPoiLngLat(poi);
        var zLevel = poi.properties.zValue || poi.properties.zLevel || 0;

        if( zLevel === 0){
            this.poiMarker.options.offZOpacity = 1;
        } else {
            this.poiMarker.options.offZOpacity = 0.3;
        }

        this.poiMarker
        .setLngLat(lngLat)
        .setZLevel(zLevel)
        .addTo(this.map);

        this.map.zLevel = zLevel;

        this.highlightRoom(poi);

        let start = this.stateController.getStart();
        let dest;
        if( poi.properties.poiId ){
            dest = { poiId: poi.properties.poiId };
        }else{
            dest = { lngLat: lngLat, zLevel: zLevel };
        }

        this.setRoute(start, dest);
        this.stateController.setState({ state: STATE_ENUMS.ROUTE, start: start, dest: dest});

        var ll = new Mazemap.LngLat( this.youAreHereDotConfig.center.lng , this.youAreHereDotConfig.center.lat);
        var bounds = ll.toBounds(3);
        bounds.extend([lngLat.lng, lngLat.lat]);
        this.map.fitBoundsRotated(bounds, {padding: {left: 200, top: 100, right: 200, bottom: 100}});
    }

    fitToFeatureBounds(geoJson: Object, padding: number){
        var bbox = Mazemap.Util.Turf.bbox(geoJson);
        // var bounds = new Mazemap.LngLatBounds([bbox[0],bbox[1]],[bbox[2],bbox[3]]);
        // bounds.extend([this.youAreHereDotConfig.center.lng, this.youAreHereDotConfig.center.lat]);
        this.map.fitBoundsRotated(bbox, {padding: padding || 0});
    }

    onStateChange (){
        const url = generateKioskShareUrlFromState( this.stateController.getState() );
        this.qrCodeBox.setUrl(url);
    }

    reset () {
        if(this.initOptions.resetUrl){
            location.href = this.initOptions.resetUrl;
        }else{
            location.reload();
        }
    }

}


// WEBPACK FOOTER //
// src/kiosk/KioskApp/index.js